import { useEffect, useRef, useState } from 'react';

import { promptText } from '../../lib/prompt-dialog.js';
import { usePixel } from '../../state/pixel.js';
import { IconButton } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { FrameThumb } from './PixelCanvas.js';

/** Plays the previewed animation (or every frame) while `playing`. */
function usePlayback() {
  const playing = usePixel((s) => s.playing);
  const anim = usePixel((s) => s.previewAnimation);
  const step = useRef(0);
  useEffect(() => {
    if (!playing) return;
    const s = usePixel.getState();
    const a = anim ? s.doc?.animations[anim] : undefined;
    const frames = a?.frames.length ? a.frames : (s.doc?.cells.map((_, i) => i) ?? []);
    if (!frames.length) return;
    const fps = a?.fps ?? 8;
    step.current = Math.max(0, frames.indexOf(s.frame));
    const timer = setInterval(() => {
      const st = usePixel.getState();
      step.current += 1;
      if (step.current >= frames.length) {
        if (a && !a.loop) {
          st.set({ playing: false });
          return;
        }
        step.current = 0;
      }
      st.setFrame(frames[step.current]!);
    }, 1000 / Math.max(0.5, fps));
    return () => clearInterval(timer);
  }, [playing, anim]);
}

function parseFrames(text: string, count: number): number[] {
  const out: number[] = [];
  for (const part of text.split(/[\s,]+/)) {
    const range = part.match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
      const a = Number(range[1]);
      const b = Number(range[2]);
      for (let i = a; a <= b ? i <= b : i >= b; i += a <= b ? 1 : -1) if (i < count) out.push(i);
    } else if (/^\d+$/.test(part) && Number(part) < count) out.push(Number(part));
  }
  return out;
}

function AnimationRow({ name }: { name: string }) {
  const anim = usePixel((s) => s.doc?.animations[name]);
  const count = usePixel((s) => s.doc?.cells.length ?? 0);
  const preview = usePixel((s) => s.previewAnimation);
  const playing = usePixel((s) => s.playing);
  const [text, setText] = useState<string | null>(null);
  const s = usePixel.getState();
  if (!anim) return null;
  const isPlaying = playing && preview === name;
  return (
    <div className={`pixel-anim${preview === name ? ' is-active' : ''}`}>
      <button
        type="button"
        className="ibtn"
        title={isPlaying ? 'Stop' : `Preview ${name}`}
        onClick={() => {
          if (isPlaying) s.set({ playing: false });
          else {
            if (anim.frames[0] !== undefined) s.setFrame(anim.frames[0]);
            s.set({ previewAnimation: name, playing: true });
          }
        }}
      >
        <Icon name={isPlaying ? 'stop' : 'play'} size={12} />
      </button>
      <button
        type="button"
        className="pixel-anim-name"
        title="Rename"
        onClick={() => {
          void promptText('Animation name', name).then((to) => {
            if (to?.trim()) s.renameAnimation(name, to.trim());
          });
        }}
      >
        {name}
      </button>
      <input
        className="mono pixel-anim-frames"
        value={text ?? anim.frames.join(', ')}
        title="Frame numbers, e.g. 0, 1, 2 or 0-3"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text !== null) s.setAnimation(name, { ...anim, frames: parseFrames(text, count) });
          setText(null);
        }}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        aria-label={`${name} frames`}
      />
      <label className="pixel-anim-fps" title="Frames per second">
        <input
          type="number"
          min={0.5}
          max={60}
          step={0.5}
          value={anim.fps}
          onChange={(e) => s.setAnimation(name, { ...anim, fps: Math.max(0.5, Number(e.target.value) || 8) })}
        />
        <span className="muted">fps</span>
      </label>
      <label className="pixel-anim-loop" title="Loop">
        <input type="checkbox" checked={anim.loop} onChange={(e) => s.setAnimation(name, { ...anim, loop: e.target.checked })} />
        <span className="muted">loop</span>
      </label>
      <IconButton icon="trash" title={`Delete ${name}`} size={12} onClick={() => s.setAnimation(name, null)} />
    </div>
  );
}

/** Frames strip, playback, onion skin and named animations. */
export function PixelTimeline() {
  const doc = usePixel((s) => s.doc);
  const frame = usePixel((s) => s.frame);
  const onion = usePixel((s) => s.onion);
  const tiled = usePixel((s) => s.tiled);
  const playing = usePixel((s) => s.playing);
  const preview = usePixel((s) => s.previewAnimation);
  usePlayback();
  if (!doc) return null;
  const s = usePixel.getState();
  const names = Object.keys(doc.animations);
  const inPreview = new Set(preview ? (doc.animations[preview]?.frames ?? []) : []);
  return (
    <div className="pixel-timeline">
      <div className="pixel-timeline-bar">
        <span className="pixel-section-head">Frames</span>
        <IconButton
          icon={playing ? 'stop' : 'play'}
          title={playing ? 'Stop' : preview ? `Play ${preview}` : 'Play all frames'}
          shortcut="Enter"
          onClick={() => s.set({ playing: !playing })}
        />
        <IconButton icon="onion" title="Onion skin (show neighbor frames)" active={onion} onClick={() => s.set({ onion: !onion })} />
        <IconButton icon="copy" title="Tiled preview (check that a texture repeats seamlessly)" active={tiled} onClick={() => s.set({ tiled: !tiled })} />
        <span className="bar-sep" />
        <IconButton icon="plus" title="New empty frame" onClick={() => s.addFrame(false)} />
        <IconButton icon="copy" title="Duplicate frame" onClick={() => s.addFrame(true)} />
        <IconButton icon="chevronRight" title="Move frame left" className="flip-x" onClick={() => s.moveFrame(-1)} disabled={frame === 0} />
        <IconButton icon="chevronRight" title="Move frame right" onClick={() => s.moveFrame(1)} disabled={frame >= doc.cells.length - 1} />
        <IconButton icon="trash" title="Delete frame" onClick={s.deleteFrame} disabled={doc.cells.length <= 1} />
        <span className="muted pixel-frame-count">
          Frame {frame} of {doc.cells.length}
        </span>
      </div>
      <div className="pixel-frames" role="listbox" aria-label="Frames">
        {doc.cells.map((_, i) => (
          <button
            key={i}
            type="button"
            role="option"
            aria-selected={i === frame}
            className={`pixel-frame${i === frame ? ' is-active' : ''}${inPreview.has(i) ? ' in-anim' : ''}`}
            onClick={() => s.setFrame(i)}
            title={`Frame ${i}`}
          >
            <FrameThumb doc={doc} frame={i} size={44} />
            <span className="pixel-frame-num">{i}</span>
          </button>
        ))}
      </div>
      <div className="pixel-anims">
        <div className="pixel-timeline-bar">
          <span className="pixel-section-head">Animations</span>
          <span className="muted">used by sprites: play(this, "name")</span>
          <span className="bar-spacer" />
          <button
            type="button"
            className="ghost small"
            onClick={() => {
              void promptText('New animation name (e.g. idle, run, attack)', names.length ? '' : 'idle').then((name) => {
                const n = name?.trim();
                if (!n || doc.animations[n]) return;
                s.setAnimation(n, { frames: doc.cells.map((_, i) => i), fps: 8, loop: true });
                s.set({ previewAnimation: n });
              });
            }}
          >
            <Icon name="plus" size={12} /> Animation
          </button>
        </div>
        {names.length === 0 ? (
          <div className="muted pixel-empty-anims">No animations yet. Add one to name frame sequences like idle, run or jump.</div>
        ) : (
          names.map((n) => <AnimationRow key={n} name={n} />)
        )}
      </div>
    </div>
  );
}
